import { ConsoleSection } from '@/types';
import { MarkdownRenderer } from '@/components/Renderers/MarkdownRenderer';
import { MermaidRenderer } from '@/components/Renderers/MermaidRenderer';
import { ChartRenderer } from '@/components/Renderers/ChartRenderer';
import { HTMLRenderer } from '@/components/Renderers/HTMLRenderer';
import { IframeRenderer } from '@/components/Renderers/IframeRenderer';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

interface VisualizationAreaProps {
  sections: ConsoleSection[];
  onClear: () => void;
  agentMessage?: string;
}

export function VisualizationArea({ sections, onClear, agentMessage }: VisualizationAreaProps) {
  const renderSection = (section: ConsoleSection, index: number) => {
    switch (section.type) {
      case 'markdown':
        return <MarkdownRenderer key={index} section={section} />;
      case 'mermaid':
        return <MermaidRenderer key={index} section={section} />;
      case 'chart':
        return <ChartRenderer key={index} section={section} />;
      case 'html':
        return <HTMLRenderer key={index} section={section} />;
      case 'iframe':
        return <IframeRenderer key={index} section={section} />;
      default:
        return (
          <div key={index} className="text-red-500">
            Unknown section type: {(section as any).type}
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed at top */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-2xl font-bold text-foreground">Information Board</h2>
        {sections.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClear}
            className="flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {sections.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <p className="text-lg">Waiting for agent output...</p>
            <p className="text-sm mt-2">Content will appear here when the agent responds</p>
          </div>
        ) : (
          <div className="space-y-6 pb-4">
            {sections.map((section, index) => renderSection(section, index))}
          </div>
        )}
      </div>

      {/* Agent Message Box - Fixed at bottom */}
      {agentMessage && (
        <div className="mt-4 p-4 bg-muted/30 border border-border rounded-lg flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">AI</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground mb-1">Agent Message</p>
              <div className="text-xs text-foreground/90 break-words whitespace-pre-wrap max-h-[3.6rem] overflow-hidden">
                {agentMessage.replace(/-# /g, '')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
